<template>
  <v-card-text class="px-0">
    <v-card class="max-h-content-container app-chat position-relative overflow-hidden text-sm">
      <div class="chat-content-area h-full d-flex flex-column">
        <div
          v-if="activeChat.contact"
          class="h-full"
        >
          <div
            class="d-flex align-center justify-space-between px-5 py-4"
            style="height:72px"
          >
            <div class="d-flex align-center">
              <v-badge
                bottom
                overlap
                dot
                bordered
                :color="resolveAvatarBadgeVariant(activeChat.contact.status)"
                offset-x="11"
                offset-y="11"
                class="me-3 user-status-badge"
              >
                <v-avatar
                  size="38"
                  class="cursor-pointer"
                  :class="`v-avatar-light-bg ${resolveAvatarBadgeVariant(activeChat.contact.status)}--text`"
                >
                  <v-img
                    v-if="activeChat.contact.avatar"
                    :src="activeChat.contact.avatar"
                  ></v-img>
                  <span v-else>{{ avatarText(activeChat.contact.fullName) }}</span>
                </v-avatar>
              </v-badge>

              <div class="d-flex flex-column">
                <p class="mb-0 text--primary font-weight-medium">
                  {{ activeChat.contact.fullName }}
                </p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <perfect-scrollbar
            ref="refChatLogPS"
            :options="perfectScrollbarOptions"
            class="ps-chat-log h-full"
          >
            <chat-log
              :chat-data="activeChat"
              :profile-user-avatar="avatar"
            ></chat-log>
          </perfect-scrollbar>

          <v-form
            autocomplete="off"
            @submit.prevent="sendMessage"
          >
            <v-text-field
              :key="activeChat.contact.id"
              v-model="chatInputMessage"
              placeholder="Digite sua mensagem"
              solo
              class="pb-5 px-5 flex-grow-0 msg-input"
              :hint="`Mínimo de 20 caracteres`"
              height="50"
              autofocus
            >
              <template #append>
                <v-btn
                  icon
                  class="me-3"
                >
                  <v-icon>
                    {{ icons.mdiAttachment }}
                  </v-icon>
                </v-btn>
                <v-btn
                  color="primary"
                  elevation="0"
                  type="submit"
                  :icon="$vuetify.breakpoint.xsOnly"
                >
                  <template v-if="$vuetify.breakpoint.smAndUp">
                    Enviar
                  </template>
                  <template v-else>
                    <v-icon>{{ icons.mdiSendOutline }}</v-icon>
                  </template>
                </v-btn>
              </template>
            </v-text-field>
          </v-form>
        </div>
      </div>
    </v-card>
  </v-card-text>
</template>

<script>
import {
  ref, onUnmounted, nextTick,
} from '@vue/composition-api'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import { mdiAttachment, mdiSendOutline } from '@mdi/js'
import { storeToRefs } from 'pinia'
import ChatLog from './ChatLog.vue'
import store from '@/store'
import chatStoreModule from './chatStoreModule'
import useChat from './useChat'
import useUserStore from '@/store/user'

export default {
  components: {
    PerfectScrollbar,
    ChatLog,
  },

  setup() {
    const headers = [
      { text: 'ID', value: 'id' },
      { text: 'Data e hora', value: 'dataCadastro' },
      { text: 'Título', value: 'titulo' },
      { text: 'Descrição', value: 'conteudoTexto' },
      { text: 'Ator', value: 'tipoAtor.nome' },
      { text: 'Status', value: 'status.nome' },
    ]
    const isConfigurarSecaoDialogOpen = ref(false)

    const { resolveAvatarBadgeVariant } = useChat()

    const CHAT_APP_STORE_MODULE_NAME = 'app-chat'
    if (!store.hasModule(CHAT_APP_STORE_MODULE_NAME)) store.registerModule(CHAT_APP_STORE_MODULE_NAME, chatStoreModule)
    onUnmounted(() => {
      if (store.hasModule(CHAT_APP_STORE_MODULE_NAME)) store.unregisterModule(CHAT_APP_STORE_MODULE_NAME)
    })

    const perfectScrollbarOptions = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }
    const refChatLogPS = ref(null)
    const scrollToBottomInChatLog = () => {
      const scrollEl = refChatLogPS.value.$el || refChatLogPS.value
      scrollEl.scrollTop = scrollEl.scrollHeight
    }

    const chatsContacts = ref([])
    const chatInputMessage = ref('')
    const activeChat = ref({})
    const openChatOfContact = () => {
      const userId = 1
      chatInputMessage.value = ''

      store.dispatch('app-chat/getChat', { userId }).then(response => {
        activeChat.value = response.data

        // Set unseenMsgs to 0
        const contact = chatsContacts.value.find(c => c.id === userId)
        if (contact) contact.chat.unseenMsgs = 0

        nextTick(() => {
          scrollToBottomInChatLog()
        })
      })
    }
    openChatOfContact()

    const { id, avatar } = storeToRefs(useUserStore())

    const sendMessage = () => {
      if (!chatInputMessage.value) return
      const payload = {
        contactId: activeChat.value.contact.id,
        senderId: id.value,
        message: chatInputMessage.value,
      }
      store.dispatch('app-chat/sendMessage', payload).then(response => {
        const { newMessageData, chat } = response.data

        // ? If it's not undefined => New chat is created (Contact is not in list of chats)
        if (chat !== undefined) {
          activeChat.value = { chat, contact: activeChat.value.contact }
          chatsContacts.value.push({
            ...activeChat.value.contact,
            chat: {
              id: chat.id,
              lastMessage: newMessageData,
              unseenMsgs: 0,
            },
          })
        } else {
          activeChat.value.chat.chat.push(newMessageData)
        }

        chatInputMessage.value = ''

        nextTick(() => {
          scrollToBottomInChatLog()
        })
      })
    }

    return {
      headers,
      isConfigurarSecaoDialogOpen,
      resolveAvatarBadgeVariant,
      perfectScrollbarOptions,
      refChatLogPS,
      avatar,
      activeChat,
      chatInputMessage,
      sendMessage,
      icons: {
        mdiAttachment,
        mdiSendOutline,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/apps/chat.scss';
</style>
